import React, { useState, useCallback } from 'react';
import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Spinner,
  Center,
  Collapse,
  Link,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { DrawerContainer } from 'src/components/molecules/DrawerContainer';
import { useAppContext } from 'src/contexts/AppContext';
import { useModelDetailsQuery, ModelListItem } from 'src/api/models';
import { MODEL_TYPE_OPTIONS } from 'src/utils/modelTypes';
import { dayjs } from 'src/libs/dayjs';
import { MODEL_STATUS_CONFIG } from 'src/features/Settings/utils/constants';
import { formatKpiUnit } from 'src/utils/kpiUnit';
import { useCurrency } from 'src/hooks/useCurrency';

type ViewModelDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  modelId: string | null;
  onEdit: (model: ModelListItem) => void;
  onDelete: (model: ModelListItem) => void;
};

export const ViewModelDrawer: React.FC<ViewModelDrawerProps> = ({
  isOpen,
  onClose,
  modelId,
  onEdit,
  onDelete,
}) => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { code: currencyCode } = useCurrency();
  const { data: model, isLoading } = useModelDetailsQuery(customerId, modelId);
  const [isCampaignsExpanded, setIsCampaignsExpanded] = useState(false);

  const statusConfig = model ? MODEL_STATUS_CONFIG[model.status] : null;
  const includedCampaigns = model?.campaigns.filter((c) => !c.blacklist) ?? [];

  const handleToggleCampaigns = useCallback(() => {
    setIsCampaignsExpanded((prev) => !prev);
  }, []);

  const handleEdit = () => {
    if (!model) return;
    onEdit({
      id: model.id,
      modelName: model.modelName,
      status: model.status,
      createdAt: model.createdAt,
      kpiMetric: model.kpiMetric,
      kpiUnit: model.kpiUnit,
      mediaPlatforms: model.mediaPlatforms,
      campaignCount: includedCampaigns.length,
      quickRun: false,
    });
  };

  const handleDelete = () => {
    if (!model) return;
    onDelete({
      id: model.id,
      modelName: model.modelName,
      status: model.status,
      createdAt: model.createdAt,
      kpiMetric: model.kpiMetric,
      kpiUnit: model.kpiUnit,
      mediaPlatforms: model.mediaPlatforms,
      campaignCount: includedCampaigns.length,
      quickRun: false,
    });
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContainer>
        <DrawerCloseButton />

        {isLoading ? (
          <Center py={20}>
            <Spinner color="icon.green" size="lg" />
          </Center>
        ) : !model ? (
          <Center py={20}>
            <Text color="content.muted">Model not found</Text>
          </Center>
        ) : (
          <>
            <DrawerHeader pb={1} pt={12}>
              <HStack spacing={3} align="center">
                <Text
                  fontFamily="marketingPage"
                  fontWeight="bold"
                  fontSize="32px"
                  color="content.title">
                  {model.modelName}
                </Text>
                <Badge
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="medium"
                  textTransform="none"
                  bg={statusConfig?.bg}
                  color={statusConfig?.color}>
                  {statusConfig?.label}
                </Badge>
              </HStack>
            </DrawerHeader>

            <DrawerBody p={6}>
              <VStack align="stretch" spacing={4}>
                {/* KPI Setup */}
                <Box>
                  <Text
                    fontFamily="marketingPage"
                    fontWeight="bold"
                    fontSize="md"
                    color="content.title"
                    mb={2}>
                    KPI Setup
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    <HStack>
                      <Text fontSize="sm" color="content.muted" minW="80px">
                        KPI Source:
                      </Text>
                      <Text fontSize="sm" color="content.title">
                        {model.kpiSource || 'KPI template 1'}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" color="content.muted" minW="80px">
                        Metric:
                      </Text>
                      <Text fontSize="sm" color="content.title">
                        {model.kpiMetric || '-'}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" color="content.muted" minW="80px">
                        Unit:
                      </Text>
                      <Text fontSize="sm" color="content.title">
                        {formatKpiUnit(model.kpiUnit, currencyCode)}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Media in model */}
                <Box>
                  <Text
                    fontFamily="marketingPage"
                    fontWeight="bold"
                    fontSize="md"
                    color="content.title"
                    mb={2}>
                    Media in model
                  </Text>
                  <Text fontSize="sm" color="content.body">
                    {model.mediaPlatforms.length === 0
                      ? 'No media selected'
                      : model.mediaPlatforms.join(', ')}
                  </Text>
                </Box>

                {/* Campaigns linked */}
                <Box>
                  <HStack justify="space-between" align="center" mb={2}>
                    <Text
                      fontFamily="marketingPage"
                      fontWeight="bold"
                      fontSize="md"
                      color="content.title">
                      Campaigns linked ({includedCampaigns.length})
                    </Text>
                    {includedCampaigns.length > 0 && (
                      <Link
                        fontSize="sm"
                        color="icon.green"
                        cursor="pointer"
                        onClick={handleToggleCampaigns}>
                        {isCampaignsExpanded ? 'Collapse' : 'Expand'}
                      </Link>
                    )}
                  </HStack>
                  {includedCampaigns.length === 0 ? (
                    <Text fontSize="sm" color="content.body">
                      No campaigns selected
                    </Text>
                  ) : (
                    <Collapse in={isCampaignsExpanded} startingHeight={0}>
                      <VStack align="stretch" spacing={1}>
                        {includedCampaigns.map((c) => (
                          <Text
                            key={c.campaignId}
                            fontSize="sm"
                            color="content.body">
                            {c.campaignName || c.campaignId}
                          </Text>
                        ))}
                      </VStack>
                    </Collapse>
                  )}
                </Box>

                {/* Info */}
                <Box>
                  <Text
                    fontFamily="marketingPage"
                    fontWeight="bold"
                    fontSize="md"
                    color="content.title"
                    mb={2}>
                    Info
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    <HStack>
                      <Text fontSize="sm" color="content.muted" minW="80px">
                        Type:
                      </Text>
                      <Text fontSize="sm" color="content.title">
                        {MODEL_TYPE_OPTIONS.find(
                          (o) => o.value === model.modelType,
                        )?.label ?? '-'}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" color="content.muted" minW="80px">
                        Created:
                      </Text>
                      <Text fontSize="sm" color="content.title">
                        {dayjs(model.createdAt).format('MMM DD, YYYY')}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" color="content.muted" minW="80px">
                        Status:
                      </Text>
                      <Text fontSize="sm" color="content.title">
                        {statusConfig?.label}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </DrawerBody>

            {/* Footer buttons (Figma design) */}
            <Box p={6} display="flex" gap={4}>
              <Button
                buttonName="delete-model"
                onClick={handleDelete}
                flex={1}
                bg="surface.panel"
                border="1px solid"
                borderColor="background.red.500"
                color="background.red.500"
                _hover={{ bg: 'red.50', borderColor: 'icon.red' }}>
                Delete Model
              </Button>
              <Button
                variant="primary"
                buttonName="edit-model"
                onClick={handleEdit}
                flex={1}>
                Edit Model
              </Button>
            </Box>
          </>
        )}
      </DrawerContainer>
    </Drawer>
  );
};
